<template>
  <v-dialog
    :value="value"
    max-width="620px"
    persistent
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="font-weight-medium secondary--text primary py-3">
        <v-icon class="mr-2" color="secondary" small>
          mdi-file-document-check-outline
        </v-icon>
        <span>{{ readonly ? "Invoice Details" : "Raise Service Invoice" }}</span>
        <v-spacer />
        <v-btn icon x-small :disabled="submitting" @click="close">
          <v-icon x-small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <div class="svi-steps pa-3">
        <span v-for="(label, index) in stepLabels" :key="label" class="svi-steps__group">
          <span
            class="svi-steps__item"
            :class="{ 'svi-steps__item--active': step === index + 1 }"
          >
            {{ label }}
          </span>
          <span v-if="index < stepLabels.length - 1" class="svi-steps__sep">/</span>
        </span>
      </div>

      <v-divider />

      <v-card-text class="svi-content">
        <div v-show="step === 1">
          <div class="svi-meta">
            <div class="svi-meta__row">
              <span>Partner</span><strong>{{ context.partnerName }}</strong>
            </div>
            <div class="svi-meta__row">
              <span>Batch</span><strong>{{ context.batchLabel }}</strong>
            </div>
            <div class="svi-meta__row">
              <span>Period</span><strong>{{ context.periodLabel }}</strong>
            </div>
          </div>

          <table class="svi-table">
            <thead>
              <tr>
                <th>Service Category</th>
                <th class="svi-num">Rate</th>
                <th class="svi-num">Gross</th>
                <th class="svi-num">GST</th>
                <th class="svi-num">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in chargedLines" :key="line.key">
                <td>{{ line.name }}</td>
                <td class="svi-num">{{ line.rate }}%</td>
                <td class="svi-num">{{ money(line.gross_amount) }}</td>
                <td class="svi-num">{{ money(line.gst_amount) }}</td>
                <td class="svi-num">{{ money(line.net_amount) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td class="svi-num"></td>
                <td class="svi-num">{{ money(context.totals.gross_amount) }}</td>
                <td class="svi-num">{{ money(context.totals.gst_amount) }}</td>
                <td class="svi-num">{{ money(context.totals.net_amount) }}</td>
              </tr>
            </tfoot>
          </table>

          <v-text-field
            v-model.trim="invoiceNumber"
            label="Invoice Number"
            placeholder="018/2026-2027"
            outlined
            dense
            hide-details="auto"
            class="mt-4"
            :disabled="readonly"
          />
        </div>

        <TransactionDetailsForm
          v-show="step === 2"
          ref="detailsForm"
          v-model="form"
          :readonly="readonly"
        />

        <div v-if="step === 3" class="svi-confirm">
          <v-icon size="40" color="warning">mdi-alert-circle-outline</v-icon>
          <p class="svi-confirm__title">Raise this invoice?</p>
          <p class="svi-confirm__text">
            The {{ context.totals.collection_count }} collections behind this
            working are locked to this invoice, so a later run for
            {{ context.batchLabel }} will not bill them again.
          </p>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-3">
        <v-btn v-if="step > 1" text small :disabled="submitting" @click="step -= 1">
          <v-icon small left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
        <v-spacer />
        <v-btn text small :disabled="submitting" @click="close">Cancel</v-btn>
        <v-btn
          v-if="step < 3"
          small
          depressed
          color="primary"
          :disabled="submitting"
          @click="goNext"
        >
          Next
        </v-btn>
        <v-btn
          v-else
          small
          depressed
          color="primary"
          :loading="submitting"
          @click="submit"
        >
          Raise Invoice
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import TransactionDetailsForm from "@/components/revenueSplit/TransactionDetailsForm.vue";

const emptyTransaction = () => ({
  transaction_id: "",
  transaction_date: "",
  utr_number: "",
  bank_account_number: "",
  bank_name: "",
  payment_mode: "",
  remarks: "",
});

export default {
  components: { TransactionDetailsForm },

  props: {
    value: { type: Boolean, default: false },
    context: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
  },

  data() {
    return {
      step: 1,
      submitting: false,
      invoiceNumber: "",
      form: emptyTransaction(),
    };
  },

  computed: {
    stepLabels() {
      return ["Working", "Settlement", "Confirm"];
    },

    chargedLines() {
      return (this.context.categories || []).filter(
        (line) => Number(line.gross_amount) > 0,
      );
    },
  },

  watch: {
    value(opened) {
      if (!opened) return;
      this.step = 1;
      this.form = emptyTransaction();
      this.invoiceNumber = "";
      this.$nextTick(() => this.$refs.detailsForm?.reset());
    },
  },

  methods: {
    close() {
      this.$emit("input", false);
    },

    goNext() {
      if (this.step === 2 && this.hasTransactionInput()) {
        if (!this.$refs.detailsForm.validate()) {
          this.$store.commit("showSnackbar", {
            message: "Please complete the transaction details",
            color: "error",
          });
          return;
        }
      }
      this.step += 1;
    },

    hasTransactionInput() {
      return Object.values(this.form).some(
        (value) => String(value || "").trim() !== "",
      );
    },

    async submit() {
      this.submitting = true;
      try {
        await this.$axios.post("/revenue_service_invoices", {
          partner_id: this.context.partnerId,
          from_date: this.context.fromDate,
          to_date: this.context.toDate,
          batch_year: this.context.batchYear,
          batch_no: this.context.batchNo,
          include_prior_unbilled: this.context.includePriorUnbilled,
          invoice_number: this.invoiceNumber || null,
          transaction_details: this.hasTransactionInput() ? this.form : {},
        });

        this.$store.commit("showSnackbar", {
          message: "Service invoice raised successfully",
          color: "success",
        });

        this.close();
        this.$emit("saved");
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message:
            err?.response?.data?.message ||
            this.changesetError(err) ||
            "Unable to raise the service invoice",
          color: "error",
        });
      } finally {
        this.submitting = false;
      }
    },

    changesetError(err) {
      const errors = err?.response?.data?.errors;
      if (!errors) return null;
      return Object.entries(errors)
        .map(([field, messages]) => `${field} ${[].concat(messages).join(", ")}`)
        .join("; ");
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.svi-steps {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.svi-steps__group {
  display: inline-flex;
  align-items: center;
}

.svi-steps__item {
  color: #98a2b3;
}

.svi-steps__item--active {
  color: var(--v-primary-base, #00aff1);
  font-weight: 600;
}

.svi-steps__sep {
  margin: 0 6px;
  color: #d0d5dd;
}

.svi-content {
  padding-top: 16px !important;
  min-height: 210px;
}

.svi-meta {
  margin-bottom: 12px;
}

.svi-meta__row {
  display: flex;
  justify-content: space-between;
  font-size: 12.5px;
  padding: 3px 0;
  color: #475467;
}

.svi-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.svi-table th {
  padding: 6px 8px;
  text-align: left;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #475467;
  background: #f9fafb;
  border-bottom: 1px solid #e4e7ec;
}

.svi-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f4f5f7;
  color: #475467;
}

.svi-table tfoot td {
  font-weight: 700;
  color: #101828;
  background: #f9fafb;
}

.svi-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.svi-confirm {
  text-align: center;
  padding: 16px 8px;
}

.svi-confirm__title {
  margin: 10px 0 4px;
  font-size: 15px;
  font-weight: 600;
  color: #101828;
}

.svi-confirm__text {
  margin: 0;
  font-size: 12.5px;
  color: #475467;
}
</style>
